import { useMutation, useQueryClient } from "@tanstack/react-query";
import { quickAccessUpsertMutation, type TQuickAccess, useQuickAccess } from "../keyboard/useQuickAccess";
import type { GetContextMenuSection } from "./helpers";
import { useActivePageId } from "../../../data";
import { getIcon } from "./icons";
import { useGraphQLClient } from "@package/graphql/react";
import { useUserId } from "../../../context/auth/hooks";

export const useGetMenuItemsQuickAccess = (): GetContextMenuSection => {
  const upsert = useUpsertQuickAccess();
  const quickAccess = useQuickAccess();
  const pageId = useActivePageId();
  const userId = useUserId();

  return (ctx) => {
    if (!ctx.isProduct) return [];
    return [
      {
        name: "Add to Favourites",
        icon: getIcon("keyboard"),
        subMenu: new Array(5).fill(null).map((_, i) => {
          const existingQuickAccess = quickAccess.data.find((q) => q.shortcut_number === i + 1);
          const displayName = existingQuickAccess?.productName;
          const shortcutText = `(Alt + Shift + ${i + 1})`;

          return {
            icon: existingQuickAccess ? getIcon("chevron", "var(--color-neutral-on-tonal)") : undefined,
            name: displayName ? `${displayName} ${shortcutText}` : `Favourite ${i + 1} ${shortcutText}`,
            action: () => {
              upsert.mutateAsync({
                id: `${userId}-${i + 1}`,
                user_id: userId,
                column_id: ctx.entityId,
                page_id: pageId,
                product_id: ctx.productId,
                shortcut_number: i + 1,
              });
            },
          };
        }),
      },
    ];
  };
};

export const useUpsertQuickAccess = () => {
  const qry = useQueryClient();
  const userId = useUserId();
  const client = useGraphQLClient();

  return useMutation({
    mutationFn: async (qa: TQuickAccess) => {
      const mutate = await client.execute(quickAccessUpsertMutation, {
        object: {
          id: qa.id,
          user_id: qa.user_id,
          page_id: qa.page_id,
          product_id: qa.product_id,
          column_id: qa.column_id,
          shortcut_number: qa.shortcut_number,
        },
      });
      return mutate;
    },
    onMutate: (qa: TQuickAccess) => {
      qry.setQueryData(["quickAccess", userId], (old: TQuickAccess[]) => {
        if (!old) return [qa];
        const existing = old.find((x) => x.id === qa.id);
        if (existing) {
          return old.map((x) => (x.id === qa.id ? { ...x, ...qa } : x));
        }
        return [...old, qa];
      });
    },
    onSettled: () => qry.invalidateQueries({ queryKey: ["quickAccess", userId] }),
  });
};
